import { Form, Formik, type FormikHelpers } from "formik";
import { useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/common/form-field";
import { SelectField } from "@/features/settings/components/select-field";
import { TextareaField } from "@/features/admin/components/textarea-field";
import { createMedia, updateMedia } from "@/features/admin/api";
import type { MediaAsset, MediaAssetType } from "@/features/admin/types";
import { ApiError } from "@/lib/api";
import { applyApiErrors } from "@/lib/form-errors";

interface FormValues {
  assetType: MediaAssetType;
  description: string;
  slug: string;
  videoId: string;
  href: string;
  detail: string;
}

interface MediaNodeDialogProps {
  /** Editing an existing node, or null when adding. */
  node: MediaAsset | null;
  /** Parent for a new node (null = root). Ignored when editing. */
  parentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function MediaNodeDialog({ node, parentId, open, onOpenChange, onSaved }: MediaNodeDialogProps) {
  const isEdit = !!node;
  // Holds the pending values when a duplicate-URL (409) confirm is required.
  const [duplicate, setDuplicate] = useState<{
    values: FormValues;
    helpers: FormikHelpers<FormValues>;
  } | null>(null);

  async function submit(values: FormValues, helpers: FormikHelpers<FormValues>, override = false) {
    const input = {
      parent: isEdit ? node!.parent : parentId,
      assetType: values.assetType,
      description: values.description,
      slug: values.slug,
      videoId: values.videoId,
      href: values.href,
      detail: values.detail,
      override,
    };
    try {
      if (isEdit) await updateMedia(node!.id, input);
      else await createMedia(input);
      setDuplicate(null);
      onSaved();
      onOpenChange(false);
      toast.success(isEdit ? "Saved." : "Added.");
    } catch (err) {
      // Duplicate LINK-URL soft warning: the API returns 409; confirm to add anyway.
      if (err instanceof ApiError && err.status === 409 && !override) {
        setDuplicate({ values, helpers });
        return;
      }
      toast.error(applyApiErrors(err, helpers, { fallback: "Couldn't save." }));
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit node" : "Add node"}</DialogTitle>
          </DialogHeader>
          <Formik<FormValues>
            initialValues={{
              assetType: node?.assetType && node.assetType !== "SLINK" ? node.assetType : "DIR",
              description: node?.description ?? "",
              slug: node?.slug ?? "",
              videoId: node?.videoId ?? "",
              href: node?.href ?? "",
              detail: node?.detail ?? "",
            }}
            validationSchema={Yup.object({
              description: Yup.string().trim().required("A title is required").max(120),
            })}
            onSubmit={(values, helpers) => submit(values, helpers)}
          >
            {({ isSubmitting, values }) => (
              <Form className="grid gap-4">
                {!isEdit && (
                  <SelectField
                    name="assetType"
                    label="Type"
                    options={[
                      { value: "DIR", label: "Folder" },
                      { value: "VIDEO", label: "Video" },
                      { value: "PDF", label: "PDF" },
                      { value: "LINK", label: "Link" },
                      { value: "TEXT", label: "Text" },
                    ]}
                  />
                )}
                <FormField name="description" label="Title" placeholder="Title" />
                {values.assetType === "DIR" && (
                  <FormField name="slug" label="Slug" placeholder="short-slug" />
                )}
                {values.assetType === "VIDEO" && (
                  <>
                    <FormField name="videoId" label="Video ID" placeholder="Video ID" />
                    <FormField name="href" label="Embed URL" placeholder="https://…" />
                  </>
                )}
                {(values.assetType === "PDF" || values.assetType === "LINK") && (
                  <FormField name="href" label="URL" placeholder="https://…" />
                )}
                {values.assetType === "TEXT" && (
                  <TextareaField name="detail" label="Text" placeholder="Content" />
                )}
                <DialogFooter>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Saving…" : isEdit ? "Save" : "Add"}
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!duplicate} onOpenChange={(o) => !o && setDuplicate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate link URL</AlertDialogTitle>
            <AlertDialogDescription>
              A link with this URL already exists in this folder. Add it anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (duplicate) void submit(duplicate.values, duplicate.helpers, true);
              }}
            >
              Add anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
